import React, {
  useState,
  useRef,
  useMemo,
  useCallback,
  useEffect
} from 'react';
import { TimelineContent, Dot } from '@components/Timeline';
import LoanPurposeButtons from './LoanPurposeButtons';
import LoanDetails from './LoanDetails';
import Inputs, { RackRateInputs } from './Inputs';
import DashedLine from '@components/DashedLine';
import * as styles from './Controls.module.scss';
import classNames from 'classnames';
import { getState, compareLoanDetails, isRefinance } from '@helpers';
import { keys, field } from '@enums';
import { isPlainObject, uniqBy } from 'lodash';
import tracker from '@utils/tracker';
import { MAX_CACHED_ZIP_CODES } from '@constants';

export default function Controls({
  state,
  setState,
  inputsAreOpen,
  defaultLoanPurpose,
  validations,
  apiErrors,
  showRackRates,
  rackRateLoanPurpose,
  rackRateLoanType,
  setRackRateLoanPurpose,
  setRackRateLoanType,
  setInputsRect,
  setInputsAreOpen,
  prefetchScenario
}) {
  //////////////////////////
  // State
  //////////////////////////

  const [_currentState, _setCurrentState] = useState(() => ({
    ...state
  }));

  useEffect(() => {
    _setCurrentState(state);
  }, [state]);

  const setCurrentState = useCallback((value, name) => {
    _setCurrentState(state => getState(state, value, name));
  }, []);

  const [_errors, setErrors] = useState([]);

  const errors = useMemo(() => {
    if (!Array.isArray(_errors)) {
      return _errors;
    }

    return isRefinance(state.loanPurpose)
      ? _errors
      : _errors.filter(({ name }) => name !== keys.CASH_OUT_AMOUNT);
  }, [state.loanPurpose, _errors]);

  const [cachedZipCodes, setCachedZipCodes] = useState({
    [String(state.zipCode)]: { isValid: true }
  });

  const addCachedZipCode = useCallback((zipCode, data) => {
    if (!zipCode || !isPlainObject(data)) {
      return;
    }

    setCachedZipCodes(zipCodes => {
      if (zipCodes[zipCode]?.isValid === data.isValid) {
        return zipCodes;
      }

      const newZipCodes = { ...zipCodes };
      const keys = Object.keys(newZipCodes);

      if (keys.length >= MAX_CACHED_ZIP_CODES - 1) {
        delete newZipCodes[keys[0]];
      }

      newZipCodes[zipCode] = data;

      return newZipCodes;
    });
  }, []);

  const canRefresh = useMemo(() => {
    const hasChanged = !compareLoanDetails(
      _currentState,
      state,
      Object.values(field).filter(k => {
        // Exclude loan purpose from comparison, and only
        // compare cash out amount if refinance is selected
        return (
          k !== keys.LOAN_PURPOSE &&
          (isRefinance(state.loanPurpose) || k !== keys.CASH_OUT_AMOUNT)
        );
      })
    );

    const hasErrors = !!errors?.length;

    if (!hasChanged || hasErrors) {
      return false;
    }

    // It takes a split second to check whether
    // a zip code is valid because it requires
    // an API request. To avoid a flash between
    // when the user clicks refresh and when
    // an error is thrown, we disable refreshing
    // unless the zip code starts with '9', which
    // means it is likely a California zip code
    const zipCode = String(_currentState.zipCode);

    if (!cachedZipCodes.hasOwnProperty(zipCode)) {
      return zipCode[0] === '9';
    }

    return true;
  }, [state, _currentState, errors, cachedZipCodes]);

  const currentState = useMemo(() => {
    return { ..._currentState, loanPurpose: state.loanPurpose };
  }, [state.loanPurpose, _currentState]);

  const shouldTrackOpenInputs = useRef(true);

  useEffect(() => {
    if (inputsAreOpen && shouldTrackOpenInputs.current) {
      shouldTrackOpenInputs.current = false;
      tracker.track(tracker.actions.OPEN_CONTROLS);
    }
  }, [inputsAreOpen]);

  useEffect(() => {
    shouldTrackOpenInputs.current = true;
  }, [state]);

  //////////////////////////
  // Validations
  //////////////////////////

  const [currentInput, setCurrentInput] = useState(null);
  const [canValidate, setCanValidate] = useState([]);
  const apiValidationErrors = useRef(apiErrors?.validation ?? null);

  useEffect(() => {
    apiValidationErrors.current = apiErrors?.validation ?? null;

    if (apiErrors?.validation) {
      setInputsAreOpen(true);
    }
  }, [apiErrors, setInputsAreOpen]);

  // Persist previous footprint error while checking new zip code
  const footprintError = useRef(null);

  const updateErrors = useCallback((error = null, name) => {
    setErrors(errors => {
      if (!error && name !== keys.ZIP_CODE && apiValidationErrors.current) {
        delete apiValidationErrors.current[name];
      }

      if (name === keys.FOOTPRINT) {
        footprintError.current = error;
      } else if (error && name === keys.ZIP_CODE) {
        footprintError.current = null;
      }

      if (!error && !errors.length) {
        return errors.slice();
      }

      const newErrors = errors.slice().filter(e => e.name !== name);

      if (error) {
        newErrors.push({ name, message: error });
      }

      return newErrors;
    });
  }, []);

  const visibleErrors = useMemo(() => {
    const currentErrors = [];

    if (footprintError.current) {
      currentErrors.push({
        name: keys.FOOTPRINT,
        message: footprintError.current
      });
    }

    if (isPlainObject(apiErrors?.validation)) {
      Object.keys(apiErrors.validation).forEach(key => {
        currentErrors.push({
          name: key,
          message: apiErrors.validation[key]
        });
      });
    }

    return uniqBy(
      currentErrors.concat(
        errors.filter(({ name }) => canValidate.includes(name))
      ),
      'message'
    );
  }, [apiErrors, errors, canValidate]);

  //////////////////////////
  // Personalize Rates
  //////////////////////////

  const refreshData = useCallback(
    (prevState = currentState) => {
      if (!canRefresh || !prevState) {
        return;
      }

      if (!isPlainObject(prevState)) {
        prevState = currentState;
      }

      const newState = { ...prevState, loanPurpose: state.loanPurpose };
      const hasErrors = errors?.length;

      if (hasErrors) {
        errors.forEach(({ name }) => {
          newState[name] = state[name];
        });
      }

      setInputsAreOpen(false);
      setErrors([]);

      if (hasErrors) {
        setCurrentState(newState);
      } else {
        setState(newState);
        setCurrentState(newState);
        setCanValidate([]);

        tracker.track(tracker.actions.PERSONALIZE_RATES, {
          loan_purpose: newState.loanPurpose,
          loan_amount: parseInt(newState.loanAmount),
          home_value: parseInt(newState.homeValue),
          zip_code: newState.zipCode,
          credit_score: parseInt(newState.creditScore),
          cashOutAmount: isRefinance(newState.loanPurpose)
            ? parseInt(newState.cashOutAmount)
            : undefined
        });
      }
    },
    [
      state,
      currentState,
      errors,
      canRefresh,
      setCurrentState,
      setState,
      setInputsAreOpen,
      setCanValidate
    ]
  );

  //////////////////////////
  // Layout
  //////////////////////////

  const wrapper = useRef();
  const dotRef = React.createRef();

  return (
    <section
      ref={wrapper}
      className={classNames('section', styles.wrapper)}
    >
      <div className="content-wrapper">
        <TimelineContent className={styles.timelineContent}>
          <Dot number={2} />
          <h2>Let us know what kind of loan you’re looking for</h2>

          {!showRackRates ? (
            <>
              <div className={styles.content}>
                <div className={styles.item}>
                  <label
                    id="loan-purpose-label"
                    className={styles.itemLabel}
                  >
                    Select your Loan Type
                  </label>
                  <LoanPurposeButtons
                    loanPurpose={state.loanPurpose}
                    labelId="loan-purpose-label"
                    setState={setState}
                  />
                </div>

                <div className={classNames(styles.item, styles.line)}>
                  <div className={styles.mask} />
                  <DashedLine />
                  <Dot
                    ref={dotRef}
                    className={styles.controlDot}
                  />
                </div>

                <div className={classNames(styles.item, styles.full)}>
                  <label className={styles.itemLabel}>
                    Customize your loan
                  </label>
                  <LoanDetails
                    state={state}
                    inputsAreOpen={inputsAreOpen}
                    setInputsAreOpen={setInputsAreOpen}
                    refreshData={refreshData}
                  />
                </div>
              </div>
              <Inputs
                state={currentState}
                prevState={state}
                validations={validations}
                errors={visibleErrors}
                visibleErrors={visibleErrors}
                cachedZipCodes={cachedZipCodes}
                addCachedZipCode={addCachedZipCode}
                canValidate={canValidate}
                canRefresh={canRefresh}
                inputsAreOpen={inputsAreOpen}
                currentInput={currentInput}
                setInputsRect={setInputsRect}
                dotRef={dotRef}
                refreshData={refreshData}
                setState={setCurrentState}
                setCurrentInput={setCurrentInput}
                updateErrors={updateErrors}
                setCanValidate={setCanValidate}
                setInputsAreOpen={setInputsAreOpen}
                prefetchScenario={prefetchScenario}
              />{' '}
            </>
          ) : (
            <RackRateInputs
              defaultLoanPurpose={defaultLoanPurpose}
              rackRateLoanPurpose={rackRateLoanPurpose}
              rackRateLoanType={rackRateLoanType}
              setRackRateLoanPurpose={setRackRateLoanPurpose}
              setRackRateLoanType={setRackRateLoanType}
            />
          )}
        </TimelineContent>
      </div>
    </section>
  );
}
